import {
  bindCustomerDetailBookingsListDispatchers,
  bindCustomerDetailContactsListDispatchers,
  buildMinimalShellCustomer,
  buildShellCustomerFromListRow,
  CUSTOMER_CONTACTS_PAGE_SIZE,
  CustomerDetailPageGate,
  CustomerDetailPageShell,
  CustomerDetailTabContent,
  lookupItemsToFilterOptions,
  lookupItemsToRoleOptionStrings,
  validateCustomerLogoFile,
  useCustomerDetailBookingsListControls,
  useCustomerDetailContactsListControls,
  useCustomerDetailTabSearchParam,
  useLookup,
  useMemoryLeakPrevention,
  useNotificationSnackbar,
  getThunkErrorMessage,
} from "design-components";
import {
  BOOKINGS_LIST_SORT_ARRIVAL_UI,
  LOOKUP_MASTERS,
  lookupItemsToBookingTypeOptions,
  mapLookupValueToBookingStatusFilter,
} from "api-client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomerDetailOverlays } from "../components/customer-detail-overlays.component";
import { useCustomerDetailBookingsActions } from "../hooks/use-customer-detail-bookings-actions";
import { useCustomerDetailContactsActions } from "../hooks/use-customer-detail-contacts-actions";
import { useCustomerDetailLocationsActions } from "../hooks/use-customer-detail-locations-actions";
import { useCustomerDetailOperatorActions } from "../hooks/use-customer-detail-operator-actions";
import { resolveOperatorIdFromListRow } from "../utils/customer-detail.page.utils";
import { lookupService } from "../../../shared/services/lookup.service";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import {
  fetchCustomerContactRoles,
  fetchCustomerContacts,
  fetchCustomerLocations,
  fetchCustomerOverview,
  setCustomerContactsListQuery,
  setCustomerLocationsSearch,
  setSelectedCustomerId,
  uploadCustomerLogo,
} from "../../../shared/store/customerDetail";
import {
  selectCustomer,
  selectCustomerContactsFromIndex,
  selectCustomerContactsPageRows,
  selectCustomerContactFormInitialValues,
  selectCustomerContactEditLoading,
  selectCustomerContactFormSubmitting,
  selectCustomerContactRoles,
  selectCustomerContactsLoading,
  selectCustomerContactsRoleCodes,
  selectCustomerContactsSearch,
  selectCustomerContactsSortDescending,
  selectCustomerContactsTotal,
  selectCustomerDetailError,
  selectCustomerDetailLoading,
  selectCustomerIsActive,
  selectCustomerLocations,
  selectCustomerLocationsCanSearch,
  selectCustomerLocationsError,
  selectCustomerLocationsLoading,
  selectCustomerLocationsSearch,
  selectCustomerStatusSubmitting,
  selectCustomerUserInviteSubmitting,
  selectCustomerUsers,
} from "../../../shared/store/customerDetail/customer-detail.selector";
import { selectCustomersListLoading, selectCustomersListRows } from "../../../shared/store/customers/customers.selector";
import { fetchCustomersList } from "../../../shared/store/customers/customers.thunk";
import {
  BOOKINGS_LIST_PAGE_SIZE,
  fetchBookings,
  prepareBookingsListForCustomerScope,
  setBookingsDateRange,
  setBookingsListRange,
  setBookingsScopeCustomerId,
  setBookingsSearch,
  setBookingsSort,
  setBookingsStatusFilters,
  setBookingsBookingTypeFilters,
} from "../../../shared/store/bookings";
import {
  selectBookingsError,
  selectBookingsFromIndex,
  selectBookingsLoading,
  selectBookingsRows,
  selectBookingsSortField,
  selectBookingsSortOrder,
  selectBookingsStatusFilterValues,
  selectBookingsBookingTypeFilterValues,
  selectBookingsTotal,
} from "../../../shared/store/bookings/bookings.selector";

export const CustomerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showNotification, snackbar } = useNotificationSnackbar();
  const { activeTab, handleTabChange } = useCustomerDetailTabSearchParam();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  const customer = useAppSelector(selectCustomer);
  const users = useAppSelector(selectCustomerUsers);
  const customerLocations = useAppSelector(selectCustomerLocations);
  const customersListRows = useAppSelector(selectCustomersListRows);
  const customersListLoading = useAppSelector(selectCustomersListLoading);
  const locationsLoading = useAppSelector(selectCustomerLocationsLoading);
  const locationsError = useAppSelector(selectCustomerLocationsError);
  const locationsSearch = useAppSelector(selectCustomerLocationsSearch);
  const locationsCanSearch = useAppSelector(selectCustomerLocationsCanSearch);
  const detailLoading = useAppSelector(selectCustomerDetailLoading);
  const detailError = useAppSelector(selectCustomerDetailError);
  const customerIsActive = useAppSelector(selectCustomerIsActive);
  const statusSubmitting = useAppSelector(selectCustomerStatusSubmitting);
  const userInviteSubmitting = useAppSelector(selectCustomerUserInviteSubmitting);

  const { items: customerRoleLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.customerRoles);
  const customerRoleOptions = useMemo(
    () => lookupItemsToRoleOptionStrings(customerRoleLookupItems),
    [customerRoleLookupItems],
  );

  const bookingsRows = useAppSelector(selectBookingsRows);
  const bookingsTotal = useAppSelector(selectBookingsTotal);
  const bookingsFromIndex = useAppSelector(selectBookingsFromIndex);
  const bookingsLoading = useAppSelector(selectBookingsLoading);
  const bookingsError = useAppSelector(selectBookingsError);
  const bookingsSortField = useAppSelector(selectBookingsSortField);
  const bookingsSortOrder = useAppSelector(selectBookingsSortOrder);
  const bookingsStatusFilters = useAppSelector(selectBookingsStatusFilterValues);
  const bookingsBookingTypeFilters = useAppSelector(selectBookingsBookingTypeFilterValues);

  const contactsRows = useAppSelector(selectCustomerContactsPageRows);
  const contactsTotal = useAppSelector(selectCustomerContactsTotal);
  const contactsFromIndex = useAppSelector(selectCustomerContactsFromIndex);
  const contactsSearch = useAppSelector(selectCustomerContactsSearch);
  const contactsRoleCodes = useAppSelector(selectCustomerContactsRoleCodes);
  const contactsSortDescending = useAppSelector(selectCustomerContactsSortDescending);
  const contactRoles = useAppSelector(selectCustomerContactRoles);
  const contactsLoading = useAppSelector(selectCustomerContactsLoading);
  const contactFormSubmitting = useAppSelector(selectCustomerContactFormSubmitting);
  const contactEditLoading = useAppSelector(selectCustomerContactEditLoading);
  const contactEditInitialValues = useAppSelector(selectCustomerContactFormInitialValues);

  const contactsRoleOptions = useMemo(
    () => contactRoles.map((role) => ({ code: role.code, label: role.label })),
    [contactRoles],
  );

  const { items: bookingStatusLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.bookingStatus);
  const { items: bookingTypeLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.bookingType);
  const bookingStatusFilterOptions = useMemo(
    () => lookupItemsToFilterOptions(bookingStatusLookupItems, mapLookupValueToBookingStatusFilter),
    [bookingStatusLookupItems],
  );
  const bookingTypeFilterOptions = useMemo(
    () =>
      lookupItemsToBookingTypeOptions(bookingTypeLookupItems).map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [bookingTypeLookupItems],
  );

  const bookingsDispatchers = useMemo(
    () =>
      bindCustomerDetailBookingsListDispatchers(dispatch, {
        prepareForCustomerScope: prepareBookingsListForCustomerScope,
        setScopeCustomerId: setBookingsScopeCustomerId,
        setDateRange: setBookingsDateRange,
        setListRange: setBookingsListRange,
        setSearch: setBookingsSearch,
        setStatusFilters: setBookingsStatusFilters,
        setBookingTypeFilters: setBookingsBookingTypeFilters,
        setSort: setBookingsSort,
        fetchBookings,
      }),
    [dispatch],
  );

  const bookingsControls = useCustomerDetailBookingsListControls({
    pageSize: BOOKINGS_LIST_PAGE_SIZE,
    fromIndex: bookingsFromIndex,
    sortingField: bookingsSortField,
    sortingOrder: bookingsSortOrder,
    defaultSortField: BOOKINGS_LIST_SORT_ARRIVAL_UI,
    defaultSortOrder: "asc",
    dispatchers: bookingsDispatchers,
  });

  const contactsDispatchers = useMemo(
    () =>
      bindCustomerDetailContactsListDispatchers(dispatch, {
        fetchCustomerContactRoles,
        fetchCustomerContacts,
        setCustomerContactsListQuery,
      }),
    [dispatch],
  );

  const contactsControls = useCustomerDetailContactsListControls({
    pageSize: CUSTOMER_CONTACTS_PAGE_SIZE,
    fromIndex: contactsFromIndex,
    dispatchers: contactsDispatchers,
  });

  const listRow = useMemo(
    () => (id ? customersListRows.find((row) => row.id === id) ?? null : null),
    [customersListRows, id],
  );

  const overviewOperatorId = useMemo(
    () => resolveOperatorIdFromListRow(listRow),
    [listRow],
  );

  const shellCustomer = useMemo(() => {
    if (customer) return customer;
    if (listRow) return buildShellCustomerFromListRow(listRow);
    if (id) return buildMinimalShellCustomer(id);
    return null;
  }, [customer, id, listRow]);

  const lockedCreateBookingCustomer = useMemo(
    () =>
      customer
        ? { value: String(customer.customerId), label: customer.name.trim() || "Customer" }
        : null,
    [customer],
  );

  const locationsActions = useCustomerDetailLocationsActions({
    customerId: id,
    customer,
    customerLocations,
    dispatch,
    showNotification,
    onTabChange: handleTabChange,
  });

  const operatorActionsState = useCustomerDetailOperatorActions({
    customerId: id,
    customer,
    customerIsActive,
    dispatch,
    showNotification,
    onAssociateLocation: locationsActions.handleAssociateLocation,
    onEditLocation: locationsActions.handleEditLocation,
    onDeleteLocation: locationsActions.handleDeleteLocation,
  });

  const bookingsActions = useCustomerDetailBookingsActions({
    dispatch,
    bookingsRows,
    showNotification,
  });

  const contactsActions = useCustomerDetailContactsActions({
    customerId: id,
    customer,
    contactsRows,
    contactEditInitialValues,
    dispatch,
    showNotification,
  });

  useMemoryLeakPrevention();

  useEffect(() => {
    if (!id) return;
    dispatch(setSelectedCustomerId(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (!id) return;
    if (customersListRows.length === 0) {
      dispatch(fetchCustomersList());
    }
  }, [customersListRows.length, dispatch, id]);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchCustomerOverview({ customerId: id, operatorId: overviewOperatorId }));
  }, [dispatch, id, overviewOperatorId]);

  useEffect(() => {
    if (!id || activeTab !== "locations") return;
    dispatch(fetchCustomerLocations({ customerId: id }));
  }, [activeTab, dispatch, id]);

  useEffect(() => {
    if (!id || activeTab !== "contacts") return;
    dispatch(fetchCustomerContactRoles());
    dispatch(fetchCustomerContacts());
  }, [activeTab, dispatch, id]);

  useEffect(() => {
    setLogoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [id]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const clearLogoPreview = useCallback(() => {
    setLogoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleLocationsSearch = useCallback(
    (value: string) => {
      if (!id) return;
      dispatch(setCustomerLocationsSearch(value));
      dispatch(fetchCustomerLocations({ customerId: id, search: value }));
    },
    [dispatch, id],
  );

  const handleLogoFileSelected = useCallback(
    async (file: File) => {
      const validationError = validateCustomerLogoFile(file);
      if (validationError) {
        showNotification({
          variant: "error",
          title: "Logo upload failed",
          description: validationError,
        });
        return;
      }
      if (!id) {
        return;
      }

      setLogoPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });

      try {
        await dispatch(
          uploadCustomerLogo({
            customerId: id,
            operatorId: overviewOperatorId,
            file,
          }),
        ).unwrap();
        clearLogoPreview();
        showNotification({
          variant: "success",
          title: "Logo updated",
          description: "Customer logo has been uploaded.",
        });
      } catch (e: unknown) {
        clearLogoPreview();
        showNotification({
          variant: "error",
          title: "Logo upload failed",
          description: getThunkErrorMessage(e, "Failed to upload logo."),
        });
      }
    },
    [clearLogoPreview, dispatch, id, overviewOperatorId, showNotification],
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const gateLoading = customersListLoading && !shellCustomer;
  const headerCustomer = customer ?? shellCustomer;
  const overviewUsers = customer ? users : [];
  const overviewLoading = detailLoading || (!customer && !detailError);
  const overviewError = detailError;

  if (!id) {
    return null;
  }

  const overlaySlot = customer ? (
    <CustomerDetailOverlays
      customer={customer}
      customerLocations={customerLocations}
      customerRoleOptions={customerRoleOptions}
      contactsRoleOptions={contactsRoleOptions}
      contactEditLoading={contactEditLoading}
      contactFormSubmitting={contactFormSubmitting}
      userInviteSubmitting={userInviteSubmitting}
      statusSubmitting={statusSubmitting}
      lockedCreateBookingCustomer={lockedCreateBookingCustomer}
      bookings={bookingsActions}
      contacts={contactsActions}
      locations={locationsActions}
      operator={operatorActionsState}
      dispatch={dispatch}
      showNotification={showNotification}
    />
  ) : null;

  return (
    <CustomerDetailPageGate loading={gateLoading} error={null} customer={shellCustomer}>
      {(resolvedCustomer) => (
        <CustomerDetailPageShell
          customer={resolvedCustomer}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onBack={handleBack}
        >
          <CustomerDetailTabContent
            activeTab={activeTab}
            customer={headerCustomer ?? resolvedCustomer}
            users={overviewUsers}
            showOperatorActions
            operatorActions={operatorActionsState.operatorActions}
            overviewLoading={overviewLoading}
            overviewError={overviewError}
            locations={customerLocations}
            locationsLoading={locationsLoading && customerLocations.length === 0 && !locationsSearch.trim()}
            locationsError={locationsError}
            locationsSearch={locationsSearch}
            locationsCanSearch={locationsCanSearch}
            onLocationsSearch={handleLocationsSearch}
            bookingsRows={bookingsRows}
            bookingsLoading={bookingsLoading}
            bookingsError={bookingsError}
            bookingsSortField={bookingsSortField}
            bookingsSortOrder={bookingsSortOrder}
            statusFilterOptions={bookingStatusFilterOptions}
            bookingsStatusFilters={bookingsStatusFilters}
            bookingTypeFilterOptions={bookingTypeFilterOptions}
            bookingsBookingTypeFilters={bookingsBookingTypeFilters}
            bookingsTotal={bookingsTotal}
            bookingsPageSize={BOOKINGS_LIST_PAGE_SIZE}
            bookingsControls={bookingsControls}
            contactsRows={contactsRows}
            contactsTotal={contactsTotal}
            contactsPageSize={CUSTOMER_CONTACTS_PAGE_SIZE}
            contactsSortDescending={contactsSortDescending}
            contactsRoleOptions={contactsRoleOptions}
            contactsRoleCodes={contactsRoleCodes}
            contactsSearch={contactsSearch}
            contactsLoading={contactsLoading}
            contactsControls={contactsControls}
            onContactsAdd={contactsActions.handleContactsAdd}
            onContactsEdit={contactsActions.handleContactsEdit}
            onContactsDeleteRequest={contactsActions.handleContactsDeleteRequest}
            onBookingNumberClick={bookingsActions.handleBookingNumberClick}
            overlaySlot={overlaySlot}
            logoPreviewUrl={logoPreviewUrl}
            onLogoFileSelected={handleLogoFileSelected}
          />
          {snackbar}
        </CustomerDetailPageShell>
      )}
    </CustomerDetailPageGate>
  );
};
